import './ApplicationGroupDependencyHeatMap.scss';

import { AppDependency, ApplicationGroup, ApplicationToServer, DatabaseApplication } from '@amzn/awscat-portfolio-assessment-types';
import { Box, Spinner } from '@amzn/awsui-components-react';
import PlotlyBasic, { Data, Layout } from 'plotly.js-dist-min';
import React, { useCallback, useEffect } from 'react';
import { useState } from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';

import { constants } from '../../../common/Localization';
import { deepCopy } from '../../../common/util';
import ApplicationGroupDependencyGraphResourceDetail from './ApplicationGroupDependencyGraphResourceDetail';
import ApplicationGroupDependencyHeatMapResourceDetail from './ApplicationGroupDependencyHeatMapResourceDetail';
import { DependencyDetail, generateHeatMapData, generateHeatMapLayout, STATUS_IN_GROUPING } from './constants';

type Props = {
    loading: boolean;
    selectedDependency: DependencyDetail | null;
    onGroupDetailUpdate: (updatedGroup: ApplicationGroup) => void;
    onResourceHideToggle: (resource: DependencyDetail, hidden: boolean) => void;
    appDependencies: AppDependency[] | null;
    appServerMappings: ApplicationToServer[];
    appDatabaseMappings: DatabaseApplication[];
    group: ApplicationGroup | null;
    dependencies: AppDependency[] | null;
    dependencyDetailMap: Map<string, DependencyDetail>;
}

const PlotlyChart = createPlotlyComponent(PlotlyBasic);

function ApplicationGroupDependencyHeatMap({ loading, selectedDependency, onGroupDetailUpdate, onResourceHideToggle, 
    appDependencies, appServerMappings, appDatabaseMappings, group, dependencies, dependencyDetailMap }: Props
): JSX.Element {

    const [ graphData, setGraphData ] = useState<Partial<Data>[]>([]);
    const [ graphLayout, setGraphLayout ] = useState<Partial<Layout>>({});
    const [ selectedHeatMap, setSelectedHeatMap ] = useState<AppDependency[] | null>(null);

    const hasData = (): boolean => {
        return (!!group && !!group.applications.length && !!dependencies);
    };

    const onHeatMapClick = (points: PlotlyBasic.PlotDatum[]) => {
        if (!group || !points || !points.length) {
            return;
        }

        const xApplication = points[0].x;
        const yApplication = points[0].y;
        // plotly defines pointIndex as a number in PlotDatum type but it is actually an array
        const index: number[] = Array.isArray(points[0].pointIndex) ? points[0].pointIndex : [];

        const data = deepCopy(graphData);
        const board: number[][] = [];
        group.applications.forEach(() => {
            board.push(Array(length).fill(0));
        });
        data[1].z = board; // reset z to 2D array of zeros
        if (index.length > 1) {
            data[1].z[index[0]][index[1]] = 1; // set to one will set the selected square to orange
        }
        setGraphData(data);
        const selectedApps = dependencies?.filter(dependency => dependency.srcAppId == xApplication && dependency.destAppId == yApplication ) ?? null;
        setSelectedHeatMap(selectedApps);
    };

    const buildHeatMap = useCallback(() => {
        if (!group || !group.applications.length || !dependencies) {
            return;
        }
        const board: number[][] = [];
        const order: { name: string[], frequency: string[] } = {
            name: [],
            frequency: [],
        };

        const selectedApp = group.applications;
        const graphDependencyMap: { [key: string]: {[key: string]: number}} = {};
        const length = selectedApp.length;

        selectedApp.forEach(application => {
            board.push(Array(length).fill(0));
            // initialize empty map
            graphDependencyMap[application.applicationId] = {};
        });

        // build dependency map
        let maxDepthCount = 0;
        dependencies.forEach(dependency => {
            const { status, isSharedInfrastructure, srcAppId, destAppId } = dependency;
            if (status === STATUS_IN_GROUPING.ACTIVE && !isSharedInfrastructure &&
                !dependencyDetailMap.get(`A ${srcAppId}`)?.hidden && !dependencyDetailMap.get(`A ${destAppId}`)?.hidden) {
                if (graphDependencyMap[srcAppId][destAppId] && graphDependencyMap[srcAppId][destAppId] !== -1) {
                    graphDependencyMap[srcAppId][destAppId] = (graphDependencyMap[srcAppId][destAppId] || 0) + 1;
                } else {
                    graphDependencyMap[srcAppId][destAppId] = 1;
                }
                graphDependencyMap[srcAppId][srcAppId] = (graphDependencyMap[srcAppId][srcAppId] || 0) + 1;
                graphDependencyMap[destAppId][destAppId] = (graphDependencyMap[destAppId][destAppId] || 0) + 1;
                maxDepthCount = Math.max(maxDepthCount, graphDependencyMap[srcAppId][srcAppId], graphDependencyMap[destAppId][destAppId]);
            }
        });

        const collator = new Intl.Collator([], { sensitivity: 'base', numeric: true });
        const appSortByName = selectedApp.sort((a,b) => collator.compare(a.name, b.name));
        appSortByName.forEach(node => order.name.push(node.applicationId));

        const appSortByFrequency = selectedApp.sort((a,b) => (
            graphDependencyMap[b.applicationId][b.applicationId] || 0) -
            (graphDependencyMap[a.applicationId][a.applicationId] || 0)
        );
        appSortByFrequency.forEach(node => order.frequency.push(node.applicationId));

        order.frequency.forEach((srcAppId, srcIdx) => {
            order.frequency.forEach((destAppId, destIdx) => {
                board[srcIdx][destIdx] = (graphDependencyMap[srcAppId][destAppId] || 0) as number;
            });
        });

        const graphData: Partial<Data>[] = generateHeatMapData(board, order);
        setGraphData(graphData);

        const graphLayout: Partial<Layout> = generateHeatMapLayout(length);
        setGraphLayout(graphLayout);
    }, [dependencies, dependencyDetailMap, group]);

    useEffect(() => {
        buildHeatMap();
    }, [ buildHeatMap ]);
    
    const getContent = () => {
        if (hasData()) {
            return (
                <Box data-testid='contentBox'>
                    <Box className='group-detail-heatmap' display='inline-block'>
                        <div id='heat-map' >
                            <PlotlyChart
                                data={graphData}
                                layout={graphLayout}
                                onClick={({ points }) => onHeatMapClick(points)}
                            />
                        </div>
                    </Box>
                    {selectedHeatMap ? (
                        <ApplicationGroupDependencyHeatMapResourceDetail
                            dependencies={selectedHeatMap}
                        />
                    ) : (
                        <ApplicationGroupDependencyGraphResourceDetail
                            groupDetail={group}
                            selectedResource={selectedDependency}
                            onGroupDetailUpdate={onGroupDetailUpdate}
                            onResourceHideToggle={onResourceHideToggle}
                            dependencies={appDependencies}
                            appServerMappings={appServerMappings}
                            appDatabaseMappings={appDatabaseMappings}
                        /> 
                    )}
                </Box>
            );
        } else {
            return (
                <Box>
                    <p className='dependency-graph-text-align' data-testid='nothingToVisualize'>{constants.NO_DEPENDENCY_TO_VISUALIZE}</p>
                </Box>
            );
        }
    };

    return loading ? (
        <Spinner size='large' />
    ) : getContent();
}

export default ApplicationGroupDependencyHeatMap;
